import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  full_name: "",
  phone_number: "",
  email: "",
  services_required: "",
  company_name: "",
  message: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useContactForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("form"); // form | otp | success
  const [loading, setLoading] = useState(false);

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isFormValid =
    form.full_name.trim().length >= 2 &&
    form.phone_number.trim().length >= 4 &&
    EMAIL_RE.test(form.email) &&
    form.services_required.trim().length >= 1 &&
    form.message.trim().length >= 1;

  const sendOTP = useCallback(async () => {
    if (!isFormValid) {
      toast.error("Please fill in all required fields with a valid email.");
      return false;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact/send-otp`, {
        email: form.email,
        full_name: form.full_name,
      });
      toast.success(`Verification code sent to ${form.email}`);
      setStep("otp");
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send verification email. Please try again.";
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [form.email, form.full_name, isFormValid]);

  const verifyAndSubmit = useCallback(async () => {
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit code.");
      return false;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact/verify-and-submit`, { ...form, otp_code: otp });
      toast.success("Verified! Your message has been delivered.");
      setStep("success");
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Verification failed. Please try again.";
      toast.error(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [form, otp]);

  const resetAll = useCallback(() => {
    setForm(INITIAL_FORM);
    setOtp("");
    setStep("form");
  }, []);

  return {
    form,
    otp,
    setOtp,
    step,
    setStep,
    loading,
    isFormValid,
    updateField,
    sendOTP,
    verifyAndSubmit,
    resetAll,
  };
}
